import PropTypes from "prop-types";
import { useEffect, useState, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useMediaQueryContext } from "lib/mediaQueryContext";
import useGoogleMaps from "lib/hooks/useGoogleMaps";
import colors from "constants/colors";
import { libraries, mapOptions } from "helpers/googleMaps";

import IconClose from "components/shared/svg/IconClose";
import AddressInput from "components/checkout/form/addressInput/AddressInput";
import StoresList from "components/nearbyStores/storesList/StoresList";

import {
    Container,
    Partial,
    Close,
    Title,
    SmallTitle,
    Maps
} from "./StoresMap.styled";

let markers = [];

function StoresMap({
    subject,
    initialAddressData,
    closeModal,
    onlyRedeemStores
}) {
    const { mobileView } = useMediaQueryContext();
    const [geocoder, setGeocoder] = useState(null);
    const [placesService, setPlacesService] = useState(null);
    const [addressData, setAddressData] = useState(initialAddressData);
    const [map, setMap] = useState(null);
    const [location, setLocation] = useState(null);
    const [stores, setStores] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [activeItem, setActiveItem] = useState(null);
    const shownResults = mobileView ? addressData?.location : true;

    const { isGoogleMapsLoaded } = useGoogleMaps({
        libraries
    });

    const methods = useForm({
        mode: "onBlur",
        defaultValues: initialAddressData
    });
    const { handleSubmit } = methods;

    const onSubmit = async data => {
        setAddressData(data);
    };

    useEffect(() => {
        if (typeof window !== "undefined" && isGoogleMapsLoaded) {
            const mapElement = document.getElementById("googleMaps");
            (() =>
                setMap(new window.google.maps.Map(mapElement, mapOptions)))();
            (() => setGeocoder(new window.google.maps.Geocoder()))();
        }
    }, [isGoogleMapsLoaded]);

    useEffect(() => {
        if (map && geocoder) {
            setStores(null);
            if (addressData?.location) {
                const address = `${addressData?.location?.postalCode ?? ""} ${
                    addressData?.location?.city ?? ""
                } NL`;

                geocoder.geocode({ address }, (results, status) => {
                    if (status === "OK") {
                        setLocation(results[0].geometry.location);
                        map.setCenter(results[0].geometry.location);
                    } else {
                        setErrorMessage(
                            "Opgegeven locatie niet gevonden op de kaart."
                        );
                    }
                });
            }
        }
    }, [map, geocoder, addressData]);

    const placeMarkers = useCallback(
        results => {
            if (!results || results === []) {
                setErrorMessage("Geen locaties gevonden in jouw omgeving");
                return;
            }
            setErrorMessage(null);

            if (markers?.length > 0) {
                markers.map(marker => marker.setMap(null));
            }
            markers = [];

            const infoWindow = new window.google.maps.InfoWindow();

            // eslint-disable-next-line array-callback-return
            results.map(result => {
                const marker = new window.google.maps.Marker({
                    position: result.geometry.location,
                    icon: "/images/icon-marker.png",
                    map
                });

                markers.push(marker);

                window.google.maps.event.addListener(
                    marker,
                    "click",
                    // eslint-disable-next-line no-shadow
                    (marker => {
                        return () => {
                            const html = `<strong>${result.name}</strong><br />${result.vicinity}`;
                            infoWindow.setContent(html);
                            infoWindow.open(map, marker);
                            map.setZoom(14);
                            map.setCenter(result.geometry.location);
                            setActiveItem(result.place_id);
                        };
                    })(marker)
                );
            });
        },
        [map]
    );

    useEffect(() => {
        if (location && typeof window !== "undefined") {
            setPlacesService(new window.google.maps.places.PlacesService(map));
        }
    }, [location, map]);

    useEffect(() => {
        setActiveItem(null);
    }, [location, addressData]);

    useEffect(() => {
        const type = onlyRedeemStores ? "merchants" : "resellers";

        async function fetchData() {
            if (placesService) {
                const places = await fetch(
                    `/api/google-maps/stores/${type}/?latlng=${location}`
                ).then(response => response.json());

                setStores(places);
                placeMarkers(places);
            }
        }
        return fetchData();
    }, [placesService, location, onlyRedeemStores, placeMarkers]);

    return (
        <Container>
            <Partial>
                <Title>{subject}en in de buurt</Title>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <AddressInput
                            name="location"
                            label={`${subject}en in de buurt`}
                            options={{
                                placeholder: mobileView
                                    ? "Vul je woonplaats"
                                    : "Vul hier je woonplaats in",
                                required: false,
                                onBlur: handleSubmit(onSubmit),
                                cityOnly: true,
                                instructions: "Selecteer een van de optie(s)"
                            }}
                        />
                    </form>
                </FormProvider>
                {isGoogleMapsLoaded && <Maps id="googleMaps" />}
            </Partial>
            {shownResults && (
                <Partial>
                    {!mobileView && (
                        <SmallTitle>
                            {`${subject}en in de buurt van ${addressData?.location?.city}`}
                        </SmallTitle>
                    )}
                    <StoresList
                        errorMessage={errorMessage}
                        stores={stores}
                        activeItem={activeItem}
                        setActiveItem={setActiveItem}
                        map={map}
                        placesService={placesService}
                    />
                </Partial>
            )}
            <Close onClick={closeModal}>
                <IconClose fill={colors.peach} />
            </Close>
        </Container>
    );
}

StoresMap.defaultProps = {
    onlyRedeemStores: false,
    subject: undefined
};

StoresMap.propTypes = {
    initialAddressData: PropTypes.object.isRequired,
    closeModal: PropTypes.func.isRequired,
    onlyRedeemStores: PropTypes.bool,
    subject: PropTypes.string
};

export default StoresMap;
